import { useState, useMemo } from 'react'
import { chartSemanticProgressColor } from '@/config/chartTheme'
import {
  Landmark,
  CheckCircle2,
  Circle,
  Minus,
  Lock,
  Unlock,
  ChevronRight,
  ClipboardList,
  Calculator,
  Banknote,
  Send,
  Loader2,
  Plus,
  History,
  FileText,
  Zap,
  Settings2,
} from 'lucide-react'
import { useAntarcticaDeals, useModuleDemoEnabled } from '@/hooks/useDemoDataAccess'
import type { Loan, ClosingChecklist, ClosingChecklistItem } from '@/types'
import ClosingLive from './ClosingLive'

/* ─── helpers ─────────────────────────────────────────────────────────── */
const fmtM = (n: number) => (n >= 1 ? `$${n.toFixed(1)}M` : `$${(n * 1000).toFixed(0)}K`)
const fmtK = (n: number) => `$${n.toLocaleString('en-US')}`
const fmtDate = (iso: string) => {
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const daysBetween = (a: string, b: string) => {
  const d1 = new Date(a + 'T00:00:00')
  const d2 = new Date(b + 'T00:00:00')
  return Math.ceil((d2.getTime() - d1.getTime()) / 86_400_000)
}

/* mock counsel names per deal */
const counselMap: Record<string, string> = {
  'ALG-1013': 'Cadwalader',
  'ALG-1014': 'Greenberg Traurig',
  'ALG-1015': 'Sidley Austin',
}

/* default closing checklist when deal doesn't have one */
const defaultChecklist: ClosingChecklist[] = [
  {
    category: 'Legal',
    items: [
      { id: 'D-1', label: 'Loan Agreement', status: 'Pending', responsible: 'Lender Counsel', dueDate: '2026-04-05' },
      { id: 'D-2', label: 'Note & Mortgage', status: 'Pending', responsible: 'Lender Counsel', dueDate: '2026-04-05' },
      { id: 'D-3', label: 'Opinion Letters', status: 'Pending', responsible: 'Borrower Counsel', dueDate: '2026-04-08' },
      { id: 'D-4', label: 'UCC Filings', status: 'Pending', responsible: 'Lender Counsel', dueDate: '2026-04-10' },
    ],
  },
  {
    category: 'Insurance',
    items: [
      { id: 'D-5', label: 'Property Insurance', status: 'Pending', responsible: 'Borrower', dueDate: '2026-04-01' },
      { id: 'D-6', label: 'Flood Certification', status: 'Pending', responsible: 'Borrower', dueDate: '2026-04-01' },
      { id: 'D-7', label: 'Title Insurance', status: 'Pending', responsible: 'Title Company', dueDate: '2026-04-03' },
      { id: 'D-8', label: 'Liability Coverage', status: 'Pending', responsible: 'Borrower', dueDate: '2026-04-01' },
    ],
  },
  {
    category: 'Title',
    items: [
      { id: 'D-9', label: 'Title Commitment', status: 'Pending', responsible: 'Title Company', dueDate: '2026-03-28' },
      { id: 'D-10', label: 'Survey', status: 'Pending', responsible: 'Surveyor', dueDate: '2026-03-25' },
      { id: 'D-11', label: 'Title Search', status: 'Pending', responsible: 'Title Company', dueDate: '2026-03-28' },
    ],
  },
  {
    category: 'Environmental',
    items: [
      { id: 'D-12', label: 'Phase I ESA', status: 'Pending', responsible: 'Environmental Firm', dueDate: '2026-03-20' },
      { id: 'D-13', label: 'Phase II ESA', status: 'Pending', responsible: 'Environmental Firm', dueDate: '2026-04-01' },
      { id: 'D-14', label: 'Environmental Insurance', status: 'Pending', responsible: 'Borrower', dueDate: '2026-04-05' },
    ],
  },
  {
    category: 'Funding',
    items: [
      { id: 'D-15', label: 'Wire Instructions', status: 'Pending', responsible: 'Borrower', dueDate: '2026-04-08' },
      { id: 'D-16', label: 'Warehouse Approval', status: 'Pending', responsible: 'Treasury', dueDate: '2026-04-09' },
      { id: 'D-17', label: 'Funding Authorization', status: 'Pending', responsible: 'Credit Committee', dueDate: '2026-04-10' },
      { id: 'D-18', label: 'Final Disbursement', status: 'Pending', responsible: 'Operations', dueDate: '2026-04-12' },
      { id: 'D-19', label: 'Recording Confirmation', status: 'Pending', responsible: 'Title Company', dueDate: '2026-04-14' },
    ],
  },
]

/* ─── sub-components ──────────────────────────────────────────────────── */

function StatusIcon({ status }: { status: ClosingChecklistItem['status'] }) {
  if (status === 'Complete')
    return <CheckCircle2 size={13} className="text-emerald-400 shrink-0" />
  if (status === 'Waived')
    return <Minus size={13} className="text-stone-500 shrink-0" />
  if (status === 'In Progress')
    return <Loader2 size={13} className="text-amber-400 shrink-0 animate-spin" />
  return <Circle size={13} className="text-stone-600 shrink-0" />
}

function ProgressBar({ pct }: { pct: number }) {
  return (
    <div className="chart-viz-track chart-viz-bar--rounded h-1.5 w-full">
      <div
        className="chart-viz-bar chart-viz-bar--rounded h-full transition-all"
        style={{ width: `${pct}%`, background: chartSemanticProgressColor(pct) }}
      />
    </div>
  )
}

/* ─── main component ──────────────────────────────────────────────────── */
/**
 * Everything the sample screen below shows — counsel, checklist, audit trail,
 * fees, the 12% mezzanine plug, wire instructions — is illustrative, so it is
 * demo-only (CRE-19, LPR-15, MOD-16). The live partition renders ClosingLive.
 */
export default function Closing() {
  const demoEnabled = useModuleDemoEnabled('closing')
  return demoEnabled ? <ClosingSample /> : <ClosingLive />
}

function ClosingSample() {
  const deals = useAntarcticaDeals('closing')
  const closingDeals = useMemo(() => {
    return deals.filter(
      (d) =>
        d.stage === 'Rate Lock' ||
        d.stage === 'IC / Committee Approved' ||
        d.closingChecklist,
    )
  }, [deals])

  const [selectedId, setSelectedId] = useState<string>(() => {
    const rl = closingDeals.find((d) => d.stage === 'Rate Lock')
    return rl?.id ?? closingDeals[0]?.id ?? ''
  })

  // Configurable checklist state (Item 10)
  const [checklistView, setChecklistView] = useState<'checklist' | 'audit'>('checklist')
  const [showAddCategory, setShowAddCategory] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState('')
  const [itemStatuses, setItemStatuses] = useState<Record<string, ClosingChecklistItem['status']>>({})

  // Audit log (full audit trail)
  const [auditLog, setAuditLog] = useState<Array<{id: string; item: string; category: string; from: string; to: string; by: string; source: string; ts: string}>>([
    { id: 'a1', item: 'Title Commitment', category: 'Title', from: 'Pending', to: 'Complete', by: 'Sarah Chen', source: 'manual', ts: '2026-04-05 09:14 AM' },
    { id: 'a2', item: 'Property Insurance', category: 'Insurance', from: 'Pending', to: 'In Progress', by: 'auto_doc_trigger', source: 'Insurance_Policy_ACORD25.pdf', ts: '2026-04-04 03:22 PM' },
    { id: 'a3', item: 'Phase I ESA', category: 'Environmental', from: 'Pending', to: 'Complete', by: 'Marcus Webb', source: 'manual', ts: '2026-04-03 11:45 AM' },
    { id: 'a4', item: 'Survey', category: 'Title', from: 'Pending', to: 'Complete', by: 'Sarah Chen', source: 'manual', ts: '2026-04-02 02:10 PM' },
  ])

  function updateItemStatus(itemId: string, itemLabel: string, category: string, newStatus: ClosingChecklistItem['status']) {
    const old = itemStatuses[itemId] || 'Pending'
    setItemStatuses(prev => ({ ...prev, [itemId]: newStatus }))
    setAuditLog(prev => [{
      id: `a${Date.now()}`,
      item: itemLabel,
      category,
      from: old,
      to: newStatus,
      by: 'Edward Buttacavoli',
      source: 'manual',
      ts: new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
    }, ...prev])
  }

  const selected = closingDeals.find((d) => d.id === selectedId) ?? closingDeals[0]

  /* resolve checklist */
  const checklist: ClosingChecklist[] = selected?.closingChecklist ?? defaultChecklist

  /* fee calculations */
  const loanAmt = (selected?.loanAmount ?? 0) * 1_000_000
  const originationFee = Math.round(loanAmt * 0.01)
  const legalCosts = 125_000
  const titleInsurance = Math.round(loanAmt * 0.002)
  const recordingFees = 12_400
  const appraisal = 15_000
  const environmental = 8_500
  const survey = 22_000
  const engineering = 18_000
  const totalCosts =
    originationFee + legalCosts + titleInsurance + recordingFees + appraisal + environmental + survey + engineering

  const propertyVal = (selected?.propertyValue ?? 0) * 1_000_000
  const seniorLoan = loanAmt
  const mezzLoan = Math.round(propertyVal * 0.12)
  const equity = propertyVal - seniorLoan - mezzLoan
  const totalSources = seniorLoan + mezzLoan + equity
  const acquisition = propertyVal
  const reserves = totalSources - acquisition - totalCosts

  const feeLines = [
    { label: 'Origination Fee (1%)', value: originationFee },
    { label: 'Legal Costs', value: legalCosts },
    { label: 'Title Insurance', value: titleInsurance },
    { label: 'Recording Fees', value: recordingFees },
    { label: 'Appraisal', value: appraisal },
    { label: 'Environmental', value: environmental },
    { label: 'Survey', value: survey },
    { label: 'Engineering', value: engineering },
  ]

  /* wire status logic */
  const wireStatus = (d: Loan) => {
    if (d.stage === 'Rate Lock') return 'Pending'
    if (d.stage === 'IC / Committee Approved') return 'N/A'
    return 'Ready'
  }

  /* funding date (mock: 26 days from today or based on rate lock expiry) */
  const fundingDate = (d: Loan) => {
    const trigger = d.triggers.find((t) => t.type === 'Rate Lock Expiry')
    if (trigger) {
      const exp = new Date(trigger.due + 'T00:00:00')
      exp.setDate(exp.getDate() - 5)
      return exp.toISOString().slice(0, 10)
    }
    const fd = new Date()
    fd.setDate(fd.getDate() + 26)
    return fd.toISOString().slice(0, 10)
  }

  const daysToClose = (d: Loan) => {
    const fd = fundingDate(d)
    return daysBetween(new Date().toISOString().slice(0, 10), fd)
  }

  return (
    <div className="px-4 py-3 space-y-3 h-full">
      <div className="rounded-md border border-amber-400/15 bg-amber-400/[0.05] px-3 py-1.5 text-[11px] text-amber-200/80">
        Sample data — illustrative counsel, checklist, audit trail, fees and wire details; none of it is recorded.
      </div>
      {/* ═══ top: closing pipeline ═══ */}
      <div className="panel">
        <div className="panel-header">
          <span className="panel-header-title flex items-center gap-2">
            <Landmark size={13} className="text-amber-400/60" />
            Closing Pipeline
          </span>
          <span className="text-[10px] text-stone-500 font-mono">
            {closingDeals.length} deals
          </span>
        </div>
        <div className="overflow-auto max-h-[220px]">
          <table className="dense-table">
            <thead>
              <tr>
                <th>Property</th>
                <th className="text-right">Amount</th>
                <th>Product</th>
                <th>Counsel</th>
                <th>Rate Lock</th>
                <th>Funding Date</th>
                <th>Wire</th>
                <th className="text-right">Days to Close</th>
              </tr>
            </thead>
            <tbody>
              {closingDeals.map((deal) => {
                const ws = wireStatus(deal)
                const dtc = daysToClose(deal)
                return (
                  <tr
                    key={deal.id}
                    className={`cursor-pointer ${deal.id === selectedId ? 'selected' : ''}`}
                    onClick={() => setSelectedId(deal.id)}
                  >
                    <td className="max-w-[180px] truncate">
                      <div className="flex items-center gap-1.5">
                        {deal.id === selectedId && (
                          <ChevronRight size={10} className="text-amber-400 shrink-0" />
                        )}
                        <span className={deal.id === selectedId ? 'text-amber-300' : ''}>
                          {deal.property}
                        </span>
                      </div>
                    </td>
                    <td className="text-right font-mono text-[11px]">{fmtM(deal.loanAmount)}</td>
                    <td className="text-[11px]">{deal.product}</td>
                    <td className="text-[11px] text-stone-400">
                      {counselMap[deal.id] ?? 'Kirkland & Ellis'}
                    </td>
                    <td>
                      {deal.stage === 'Rate Lock' ? (
                        <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400">
                          <Lock size={10} /> Locked
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] text-stone-500">
                          <Unlock size={10} /> Pending
                        </span>
                      )}
                    </td>
                    <td className="text-[11px] text-stone-400">{fmtDate(fundingDate(deal))}</td>
                    <td>
                      <span
                        className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium border ${
                          ws === 'Ready'
                            ? 'bg-emerald-400/10 text-emerald-400 border-emerald-400/20'
                            : ws === 'Pending'
                            ? 'bg-amber-400/10 text-amber-400 border-amber-400/20'
                            : 'bg-stone-500/10 text-stone-500 border-stone-500/20'
                        }`}
                      >
                        {ws}
                      </span>
                    </td>
                    <td className="text-right font-mono text-[11px]">
                      <span
                        className={
                          dtc <= 10 ? 'text-red-400' : dtc <= 20 ? 'text-amber-400' : 'text-stone-300'
                        }
                      >
                        {dtc}d
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ═══ bottom: checklist + fee calculator ═══ */}
      <div className="grid grid-cols-2 gap-3" style={{ height: 'calc(100vh - 22rem)' }}>
        {/* ─── closing checklist ─── */}
        <div className="panel min-h-0 flex flex-col">
          <div className="panel-header">
            <span className="panel-header-title flex items-center gap-2">
              <ClipboardList size={13} className="text-amber-400/60" />
              Closing Checklist
            </span>
            <div className="flex items-center gap-2">
              {/* Audit trail / checklist toggle */}
              <button
                onClick={() => setChecklistView(checklistView === 'checklist' ? 'audit' : 'checklist')}
                className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border transition-colors ${
                  checklistView === 'audit'
                    ? 'border-amber-400/30 text-amber-300 bg-amber-400/10'
                    : 'border-stone-700 text-stone-500 hover:text-stone-300'
                }`}
              >
                <History size={9} />
                {checklistView === 'audit' ? 'Back to Checklist' : 'Audit Trail'}
              </button>
              {/* Add category button */}
              {checklistView === 'checklist' && (
                <button
                  onClick={() => setShowAddCategory(!showAddCategory)}
                  className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border border-emerald-400/20 text-emerald-400 hover:bg-emerald-400/10 transition-colors"
                >
                  <Plus size={9} />
                  Add Category
                </button>
              )}
              <span className="text-[10px] text-stone-500 font-mono">{selected?.property}</span>
            </div>
          </div>

          {/* Auto-doc trigger notice */}
          {checklistView === 'checklist' && (
            <div className="flex items-center gap-2 px-3 py-1.5 border-b border-amber-400/10 bg-black/10">
              <Zap size={9} className="text-blue-400 shrink-0" />
              <span className="text-[9px] text-stone-600">
                Checklist items auto-update when matching documents are received via ingestion.
                <span className="text-blue-400 ml-1">2 items auto-triggered today.</span>
              </span>
            </div>
          )}

          {/* Add category form */}
          {showAddCategory && checklistView === 'checklist' && (
            <div className="flex items-center gap-2 px-3 py-2 border-b border-amber-400/10 bg-black/20">
              <input
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="New category name..."
                className="flex-1 bg-black/30 border border-amber-400/20 rounded px-2 py-1 text-[11px] text-stone-200 placeholder-stone-600 focus:outline-none focus:border-amber-400/40"
              />
              <button
                onClick={() => { setShowAddCategory(false); setNewCategoryName('') }}
                className="px-2 py-1 rounded border border-emerald-400/20 text-[10px] text-emerald-400 hover:bg-emerald-400/10"
              >
                Add
              </button>
            </div>
          )}

          <div className="flex-1 overflow-auto panel-body space-y-4">
            {checklistView === 'audit' ? (
              /* ─── Audit Trail (Item 10) ─── */
              <div className="space-y-2">
                <p className="text-[10px] text-stone-600">Full audit trail of all checklist status changes — manual and auto-triggered.</p>
                {auditLog.map((entry) => (
                  <div key={entry.id} className="flex items-start gap-3 px-2 py-2 rounded-lg border border-amber-400/8 bg-black/20 text-[10px]">
                    <div className="shrink-0 mt-0.5">
                      {entry.source === 'manual'
                        ? <Settings2 size={10} className="text-stone-500" />
                        : <Zap size={10} className="text-blue-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-medium text-stone-200">{entry.item}</span>
                        <span className="text-stone-600">{entry.category}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-stone-500">
                        <span className="text-red-400/70">{entry.from}</span>
                        <ChevronRight size={8} />
                        <span className="text-emerald-400">{entry.to}</span>
                        <span className="text-stone-700">•</span>
                        <span className={entry.source !== 'manual' ? 'text-blue-400' : 'text-stone-500'}>{entry.by}</span>
                        {entry.source !== 'manual' && (
                          <>
                            <span className="text-stone-700">via</span>
                            <span className="text-blue-300 flex items-center gap-0.5"><FileText size={8} />{entry.source}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <span className="text-stone-600 shrink-0 text-[9px]">{entry.ts}</span>
                  </div>
                ))}
              </div>
            ) : (
              /* ─── Checklist (with interactive status updates) ─── */
              checklist.map((cat) => {
                const items = cat.items.map(i => ({ ...i, status: itemStatuses[i.id] || i.status }))
                const complete = items.filter(i => i.status === 'Complete' || i.status === 'Waived').length
                const pctDone = Math.round((complete / items.length) * 100)
                return (
                  <div key={cat.category}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] font-medium text-stone-300">{cat.category}</span>
                      <span className={`text-[10px] font-mono ${pctDone >= 80 ? 'text-emerald-400' : pctDone >= 40 ? 'text-amber-400' : 'text-red-400'}`}>
                        {pctDone}%
                      </span>
                    </div>
                    <ProgressBar pct={pctDone} />
                    <div className="mt-2 space-y-1">
                      {items.map((item) => (
                        <div key={item.id} className="flex items-center gap-2 py-0.5 group">
                          <button
                            onClick={() => {
                              const next: ClosingChecklistItem['status'] = item.status === 'Pending' ? 'In Progress' : item.status === 'In Progress' ? 'Complete' : 'Pending'
                              updateItemStatus(item.id, item.label, cat.category, next)
                            }}
                          >
                            <StatusIcon status={item.status} />
                          </button>
                          <span className={`text-xs flex-1 ${item.status === 'Complete' ? 'text-stone-400 line-through' : item.status === 'In Progress' ? 'text-amber-300' : 'text-stone-300'}`}>
                            {item.label}
                          </span>
                          <span className="text-[9px] text-stone-600 font-mono">{item.responsible}</span>
                          {item.status === 'Pending' && (
                            <button
                              onClick={() => updateItemStatus(item.id, item.label, cat.category, 'Waived')}
                              className="hidden group-hover:inline text-[9px] text-stone-600 hover:text-stone-400 ml-1"
                            >
                              waive
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>

        {/* ─── fee calculator + sources/uses + wire ─── */}
        <div className="panel min-h-0 flex flex-col">
          <div className="panel-header">
            <span className="panel-header-title flex items-center gap-2">
              <Calculator size={13} className="text-amber-400/60" />
              Fee Calculator
            </span>
          </div>
          <div className="flex-1 overflow-auto panel-body space-y-4">
            {/* fee line items */}
            <div className="space-y-1">
              {feeLines.map((f) => (
                <div key={f.label} className="flex items-center justify-between text-xs">
                  <span className="text-stone-400">{f.label}</span>
                  <span className="font-mono text-stone-200">{fmtK(f.value)}</span>
                </div>
              ))}
              <div className="gold-divider my-2" />
              <div className="flex items-center justify-between text-xs font-semibold">
                <span className="text-amber-300">Total Closing Costs</span>
                <span className="font-mono text-amber-300">{fmtK(totalCosts)}</span>
              </div>
            </div>

            {/* sources & uses */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="h-px flex-1 bg-gradient-to-r from-amber-400/20 to-transparent" />
                <h3 className="text-[10px] font-mono uppercase tracking-[0.18em] text-stone-500 shrink-0 flex items-center gap-1.5">
                  <Banknote size={11} className="text-amber-400/50" />
                  Sources & Uses
                </h3>
                <div className="h-px flex-1 bg-gradient-to-l from-amber-400/20 to-transparent" />
              </div>

              {/* sources */}
              <div className="mb-2">
                <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-stone-500 mb-1">
                  Sources
                </div>
                {[
                  { label: 'Senior Loan', value: seniorLoan },
                  { label: 'Mezzanine', value: mezzLoan },
                  { label: 'Equity', value: equity },
                ].map((s) => (
                  <div key={s.label} className="flex justify-between text-xs py-0.5">
                    <span className="text-stone-400">{s.label}</span>
                    <span className="font-mono text-stone-200">{fmtK(s.value)}</span>
                  </div>
                ))}
              </div>

              <div className="gold-divider my-1.5" />

              {/* uses */}
              <div className="mb-2">
                <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-stone-500 mb-1">
                  Uses
                </div>
                {[
                  { label: 'Acquisition', value: acquisition },
                  { label: 'Closing Costs', value: totalCosts },
                  { label: 'Reserves', value: Math.max(reserves, 0) },
                ].map((u) => (
                  <div key={u.label} className="flex justify-between text-xs py-0.5">
                    <span className="text-stone-400">{u.label}</span>
                    <span className="font-mono text-stone-200">{fmtK(u.value)}</span>
                  </div>
                ))}
              </div>

              <div className="gold-divider my-1.5" />
              <div className="flex justify-between text-xs font-semibold">
                <span className="text-amber-300">Total</span>
                <span className="font-mono text-amber-300">{fmtK(totalSources)}</span>
              </div>
            </div>

            {/* wire instructions */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="h-px flex-1 bg-gradient-to-r from-amber-400/20 to-transparent" />
                <h3 className="text-[10px] font-mono uppercase tracking-[0.18em] text-stone-500 shrink-0 flex items-center gap-1.5">
                  <Send size={11} className="text-amber-400/50" />
                  Wire Instructions
                </h3>
                <div className="h-px flex-1 bg-gradient-to-l from-amber-400/20 to-transparent" />
              </div>

              <div className="rounded-md border border-amber-400/10 bg-black/20 px-3 py-2 space-y-1.5 text-xs">
                {[
                  { label: 'Bank', value: 'First National Bank' },
                  { label: 'ABA Routing', value: '\u2022\u2022\u2022\u20223456' },
                  { label: 'Account', value: '\u2022\u2022\u2022\u20227890' },
                  { label: 'Reference', value: `${selected?.id ?? 'ALG'}-WIRE` },
                ].map((w) => (
                  <div key={w.label} className="flex justify-between">
                    <span className="text-stone-500">{w.label}</span>
                    <span className="font-mono text-stone-300">{w.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
